import {Autocomplete, Box, createFilterOptions, Stack, TextField, Typography} from "@mui/material";
import {HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {KeySnapshot, KeyVaultRef} from "../../api/Model";
import {TextFieldProps} from "@mui/material/TextField/TextField";
import {AutocompleteRenderInputParams} from "@mui/material/Autocomplete/Autocomplete";
import {autocompleteChangeEvent} from "../../util/FormHandlers";
import {useRemoteValue} from "../../util/RemoteValue";
import {useVaultKeysStore, VaultKeys} from "../../api/VaultKeysStore";
import {emptyKey} from "../../api/Empty";
import {KeyLocationShield} from "../location/KeyLocationShield";
import {stringifyKey} from "../../pages/manage-keys/KeyTableFilter";
import {listenCustomWindowEvent, useEffectWithLifetime} from "../../util/Lifetime";
import {NFC_KEY_DETECTED_EVENT, NFC_KEY_READING_EVENT} from "../../util/NfcReaderListener";
import {NfcScanIcon} from "../nfc-scan-icon/NfcScanIcon";
import {KeyTagChip} from "../key-tag-chip/KeyTagChip";
import {BindNfcPopover} from "./BindNfcPopover";

const MAX_VISIBLE_OPTIONS = 100;

const defaultFilter = createFilterOptions<KeySnapshot>({
    stringify: keySnapshot => stringifyKey(keySnapshot.key, [keySnapshot.historyRecord]),
});

const moreHint = {...emptyKey};

function isMoreHint(option: KeySnapshot) {
    return option === moreHint;
}

const filterOptions = (options: KeySnapshot[], state: {
    inputValue: string;
    getOptionLabel: (option: KeySnapshot) => string
}) => {
    const filtered = defaultFilter(options, state);
    if (filtered.length <= MAX_VISIBLE_OPTIONS) return filtered;
    const limited = filtered.slice(0, MAX_VISIBLE_OPTIONS);
    limited.push(moreHint);
    return limited;
};

function KeyEndAdornment({keySnapshot}: { keySnapshot: KeySnapshot }) {
    return (
        <>
            <Stack direction="row" gap={0.5}>
                {keySnapshot.key.tags.map((tag) => (
                    <KeyTagChip keyTag={tag} key={tag.id} size="small"/>
                ))}
            </Stack>
            <KeyLocationShield location={keySnapshot.historyRecord.location}/>
        </>
    );
}

const optionKey = (option: KeySnapshot) => `${option.key.id}-${option.historyRecord.id}`;
const optionLabel = (option: KeySnapshot) => option === emptyKey || isMoreHint(option) ? '' : option.key.keyNumber.toString();
const renderOption = (optionProps: HTMLAttributes<HTMLLIElement> & { key: any }, keyState: KeySnapshot) => {
    if (isMoreHint(keyState)) {
        return (
            <Box {...optionProps} key={optionProps.key} component="li" sx={{width: '100%', pointerEvents: 'none'}}>
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    Type to see more keys...
                </Typography>
            </Box>
        );
    }
    return (
        <Box {...optionProps} key={optionProps.key} component="li" sx={{width: '100%'}}>
            <Stack width="100%" direction="row" gap={1} alignItems="center" justifyContent="space-between">
                <Box>{keyState.key.keyNumber}</Box>
                <Stack direction="row" gap={1} alignSelf="end">
                    <KeyEndAdornment keySnapshot={keyState}/>
                </Stack>
            </Stack>
        </Box>
    );
};

export function KeySelect(
    {
        keySnapshot,
        setKeySnapshot,
        filterKeys = (vaultKeys: VaultKeys) => vaultKeys.allKeys(),
        ...props
    }: {
        keySnapshot?: KeySnapshot,
        setKeySnapshot: (newValue: KeySnapshot | undefined) => void,
        filterKeys?: (vaultKeys: VaultKeys) => KeySnapshot[],
    } & TextFieldProps
) {
    const vaultKeys = useVaultKeysStore(state => state.remoteValue);
    const allKeysRV = useMemo(
        () => vaultKeys.map(filterKeys),
        [vaultKeys, filterKeys]
    );
    const allKeys = useRemoteValue(allKeysRV, []);
    const [nfcReadingProgress, setNfcReadingProgress] = useState<number>(0);
    const [nfcReadingError, setNfcReadingError] = useState<string>();
    const [unknownTransponderId, setUnknownTransponderId] = useState<string | undefined>(undefined);
    const [bindPopoverOpen, setBindPopoverOpen] = useState(false);
    const nfcIconRef = useRef<Element>(null);

    // Listen for global NFC events
    useEffectWithLifetime(lifetime => {
        listenCustomWindowEvent(lifetime, NFC_KEY_DETECTED_EVENT, async (event: CustomEvent<string>) => {
            const transponderId = event.detail;
            const allKeys = await vaultKeys.value;
            const matchingKeys = await allKeysRV.value;
            const key = allKeys.byNFC(transponderId);
            if (key !== undefined) {
                if (matchingKeys.some(k => k.key.id === key.key.id && k.historyRecord.id === key.historyRecord.id)) {
                    setKeySnapshot(key);
                } else {
                    setNfcReadingError(`Scanned key ${key.key.keyNumber} is not in the list`);
                }
            } else {
                setKeySnapshot(undefined);
                setUnknownTransponderId(transponderId);
                setBindPopoverOpen(true);
            }
        });
        listenCustomWindowEvent(lifetime, NFC_KEY_READING_EVENT, async (event: CustomEvent<number>) => {
            setNfcReadingProgress(Math.floor(event.detail * 100));
        });
    }, [vaultKeys, setKeySnapshot, setNfcReadingProgress, setNfcReadingError, allKeysRV]);

    useEffect(() => {
        setNfcReadingError(undefined);
        setUnknownTransponderId(undefined);
    }, [setNfcReadingError, keySnapshot]);

    const renderInput = useCallback((params: AutocompleteRenderInputParams) => {
        const selectedKey = keySnapshot && keySnapshot !== emptyKey ? keySnapshot : undefined;
        const textFieldInputProps = props.InputProps ?? {};
        const nfcError = unknownTransponderId ? 'Unknown NFC tag — click to assign' : nfcReadingError;

        return (
            <TextField
                {...params}
                {...props}
                slotProps={{
                    input: {
                        ...params.InputProps,
                        ...props.slotProps?.input,
                        startAdornment: (
                            <NfcScanIcon
                                pulse={!!nfcReadingProgress}
                                error={nfcError}
                                onClick={unknownTransponderId ? (() =>  setBindPopoverOpen(true)) : undefined}
                            />
                        ),
                        endAdornment: (
                            <Stack direction="row" alignItems="center" gap={1}>
                                {selectedKey && (
                                    <KeyEndAdornment keySnapshot={selectedKey}/>
                                )}
                                {textFieldInputProps.endAdornment}
                                {params.InputProps.endAdornment}
                            </Stack>
                        )
                    }
                }}
            />
        );
    }, [props, keySnapshot, unknownTransponderId, nfcReadingProgress, nfcReadingError]);

    const handleKeyChange = useMemo(() => autocompleteChangeEvent(
        (newValue: KeySnapshot) => setKeySnapshot(newValue !== emptyKey ? newValue : undefined),
    ), [setKeySnapshot]);

    useEffect(() => {
        if (keySnapshot !== undefined && !allKeys.some(key => key.key.id === keySnapshot.key.id && key.historyRecord.id === keySnapshot.historyRecord.id)) {
            setKeySnapshot(emptyKey);
        }
    }, [keySnapshot, setKeySnapshot, allKeys]);

    return (
        <>
            <Autocomplete
                options={allKeys}
                filterOptions={filterOptions}
                getOptionKey={optionKey}
                getOptionLabel={optionLabel}
                getOptionDisabled={isMoreHint}
                renderInput={renderInput}
                renderOption={renderOption}
                value={keySnapshot || emptyKey}
                onChange={handleKeyChange}
                disableClearable={true}
                ref={nfcIconRef}
            />
            <BindNfcPopover
                open={bindPopoverOpen}
                anchorEl={nfcIconRef.current}
                allKeys={allKeys}
                transponderId={unknownTransponderId}
                onClose={() => setBindPopoverOpen(false)}
                onBind={(boundKey) => {
                    setUnknownTransponderId(undefined);
                    setBindPopoverOpen(false);
                    setKeySnapshot(boundKey);
                }}
            />
        </>
    );
}

export function KeySelectInVault({keyVault, keyNumber, setKeyNumber, ...props}: {
    keyVault: KeyVaultRef,
    keyNumber?: string,
    setKeyNumber: (keyNumber?: string) => void,
} & TextFieldProps) {
    const [keySnapshot, setKeySnapshot] = useState<KeySnapshot | undefined>(undefined);

    const filterKeys = useCallback(
        (vaultKeys: VaultKeys) => vaultKeys.inVault(keyVault),
        [keyVault]
    );

    useEffect(() => {
        setKeyNumber(keySnapshot?.key.keyNumber);
    }, [keySnapshot, setKeyNumber]);

    return (
        <KeySelect
            keySnapshot={keySnapshot}
            setKeySnapshot={setKeySnapshot}
            filterKeys={filterKeys}
            {...props}
        />
    );
}
